import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { DoorOpen, Plus, QrCode, Download, Printer, Power, Users, Clock, Loader2, ExternalLink, Trash2 } from "lucide-react";

const FACILITIES = ["TV", "HDMI", "Whiteboard", "Video Conference", "Projector", "Wi-Fi"];

export default function MeetingRooms() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ facilities: [] });
  const [saving, setSaving] = useState(false);
  const [qr, setQr] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const canManage = ["SUPER_ADMIN", "COMPANY_ADMIN", "BRANCH_ADMIN"].includes(user.role);
  const canDelete = ["SUPER_ADMIN", "COMPANY_ADMIN"].includes(user.role);

  const load = () => api.get("/rooms").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm({ facilities: [], capacity: 6, free_duration_minutes: 60, booking_hours: "09:00-20:00", status: "active", branch_id: user.branch_id || undefined }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, capacity: Number(form.capacity), free_duration_minutes: Number(form.free_duration_minutes) };
      if (editing) await api.put(`/rooms/${editing.id}`, payload);
      else await api.post("/rooms", payload);
      toast.success(editing ? "Room updated" : "Room created"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const toggle = async (r) => { try { await api.patch(`/rooms/${r.id}/status`); load(); } catch (e) { toast.error(apiError(e)); } };
  const doDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/rooms/${delTarget.id}`);
      toast.success("Room deleted"); setDelTarget(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setDeleting(false); }
  };
  const showQr = async (r) => { try { const { data } = await api.get(`/rooms/${r.id}/qr`); setQr({ ...data, name: r.name }); } catch (e) { toast.error(apiError(e)); } };
  const toggleFac = (f) => setForm((p) => ({ ...p, facilities: p.facilities?.includes(f) ? p.facilities.filter((x) => x !== f) : [...(p.facilities || []), f] }));
  const printQr = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const doc = w.document;
    doc.title = `${qr.name} QR`;
    const wrap = doc.createElement("div");
    wrap.style.cssText = "text-align:center;font-family:sans-serif;padding:40px";
    const h = doc.createElement("h2"); h.textContent = qr.name;
    const img = doc.createElement("img"); img.src = qr.qr; img.style.width = "320px";
    const p = doc.createElement("p"); p.textContent = qr.url;
    wrap.append(h, img, p);
    doc.body.append(wrap);
    img.onload = () => w.print();
  };

  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();

  if (!list) return <Loader />;

  return (
    <div>
      <PageHeader title="Meeting Rooms" subtitle="Each room has a unique booking QR & URL."
        actions={canManage && <Button onClick={openNew} data-testid="add-room-btn" className="bg-navy-900 hover:bg-navy-800"><Plus size={16} className="mr-1.5" /> New Room</Button>} />

      {list.length === 0 ? <EmptyState icon={DoorOpen} title="No meeting rooms yet" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((r) => {
            const cur = (r.today_bookings || []).find((b) => b.start_min <= nowMin && nowMin < b.end_min);
            const next = (r.today_bookings || []).find((b) => b.start_min > nowMin);
            return (
              <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-5 sf-fade-up" data-testid={`room-card-${r.code}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display font-bold text-navy-900 text-lg">{r.name}</h3>
                    <p className="text-xs text-slate-400 font-mono">{r.code} · {r.floor}</p>
                  </div>
                  <StatusBadge status={cur ? "BOOKED" : "AVAILABLE"} />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full flex items-center gap-1"><Users size={11} /> {r.capacity}</span>
                  {(r.facilities || []).map((f) => <span key={f} className="text-xs text-navy-800 bg-navy-50 px-2 py-0.5 rounded-full">{f}</span>)}
                </div>
                <div className="mt-3 text-sm text-slate-600 space-y-0.5">
                  <p className="flex items-center gap-1 text-emerald-700"><Clock size={12} /> Free up to {r.free_duration_minutes} min</p>
                  {cur ? <p className="text-rose-600">Now: {cur.start_time}–{cur.end_time}</p> : next ? <p className="text-slate-500">Next: {next.start_time}–{next.end_time}</p> : <p className="text-slate-400">No bookings today</p>}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => showQr(r)} data-testid={`room-qr-${r.code}`}><QrCode size={14} className="mr-1" /> QR</Button>
                  {canManage && <><Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`edit-room-${r.code}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => toggle(r)}><Power size={14} /></Button></>}
                  {canDelete && <Button size="sm" variant="outline" onClick={() => setDelTarget(r)} data-testid={`delete-room-${r.code}`} className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"><Trash2 size={14} /></Button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Room" : "New Meeting Room"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editing && user.role !== "BRANCH_ADMIN" && (
              <div><Label>Branch *</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="room-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Room Name *</Label><Input data-testid="room-name" className="mt-1 h-10" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Room Code *</Label><Input data-testid="room-code" className="mt-1 h-10" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
              <div><Label>Capacity *</Label><Input type="number" data-testid="room-capacity" className="mt-1 h-10" value={form.capacity || ""} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
              <div><Label>Floor / Location</Label><Input data-testid="room-floor" className="mt-1 h-10" value={form.floor || ""} onChange={(e) => setForm({ ...form, floor: e.target.value })} /></div>
              <div><Label>Booking Hours</Label><Input data-testid="room-hours" className="mt-1 h-10" value={form.booking_hours || ""} onChange={(e) => setForm({ ...form, booking_hours: e.target.value })} /></div>
              <div><Label>Free Duration (min)</Label><Input type="number" data-testid="room-free" className="mt-1 h-10" value={form.free_duration_minutes || ""} onChange={(e) => setForm({ ...form, free_duration_minutes: e.target.value })} /></div>
            </div>
            <div>
              <Label>Facilities</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {FACILITIES.map((f) => (
                  <button key={f} type="button" onClick={() => toggleFac(f)} data-testid={`fac-${f}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.facilities?.includes(f) ? "bg-navy-900 text-white border-navy-900" : "bg-white text-slate-600 border-slate-200"}`}>{f}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-room-btn" className="bg-navy-900">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qr} onOpenChange={(v) => !v && setQr(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Booking QR · {qr?.name}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center">
            <img src={qr?.qr} alt="QR" className="w-56 h-56" data-testid="room-qr-image" />
            <a href={qr?.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 mt-2 break-all text-center flex items-center gap-1">{qr?.url} <ExternalLink size={12} /></a>
            <div className="flex gap-2 mt-4">
              <a href={qr?.qr} download={`${qr?.name}-qr.png`}><Button variant="outline"><Download size={15} className="mr-1" /> Download</Button></a>
              <Button variant="outline" onClick={printQr}><Printer size={15} className="mr-1" /> Print</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent data-testid="delete-room-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{delTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the room and <strong>all of its bookings</strong> (past and upcoming). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-room">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="confirm-delete-room" className="bg-rose-600 hover:bg-rose-700">
              {deleting ? <Loader2 className="animate-spin" size={16} /> : "Delete Room"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
